import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, ExternalLink } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import usePageMeta from "../hooks/usePageMeta";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { BlogHeader } from "../components/blog/BlogHeader";
import { formatDate } from "../components/blog/PostCard";

const StatusPill = ({ status }) => (
  <span className={`text-xs rounded-full px-2.5 py-0.5 font-medium ${status === "published" ? "bg-[#E2F0E7] text-[#1A362D]" : "bg-[#FDF2DF] text-[#D99C3D]"}`}>
    {status === "published" ? "Published" : "Draft"}
  </span>
);

export default function BlogAdmin() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState(null);
  const [denied, setDenied] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  usePageMeta("Manage guides — Trackr");

  const load = () =>
    api.get("/blog/admin/posts").then(({ data }) => setPosts(data)).catch((e) => {
      if (e.response?.status === 403) setDenied(true);
      setPosts([]);
    });
  useEffect(() => { load(); }, []);

  const remove = async () => {
    try {
      await api.delete(`/blog/admin/posts/${toDelete.id}`);
      toast.success("Post deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F1EE] text-[#1A362D] font-body">
      <BlogHeader />
      <main className="max-w-5xl mx-auto px-6 py-12">
        {denied ? (
          <div data-testid="blog-admin-denied">
            <h1 className="font-display text-3xl">Admins only</h1>
            <p className="text-[#5C6B64] mt-3">Your account doesn't have permission to manage the blog.</p>
          </div>
        ) : (
          <>
            <div className="flex items-end justify-between gap-4 mb-10">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#C65D47]">Blog admin</p>
                <h1 className="font-display text-4xl tracking-tight mt-2">Your guides</h1>
              </div>
              <Button onClick={() => navigate("/blog/admin/new")} className="rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white px-5" data-testid="blog-admin-new-btn">
                <Plus className="w-4 h-4 mr-1" /> New post
              </Button>
            </div>
            <div className="rounded-xl border border-[#E5E1DB] bg-white divide-y divide-[#E5E1DB]" data-testid="blog-admin-list">
              {posts === null && <div className="p-6 text-[#5C6B64]">Loading…</div>}
              {posts?.length === 0 && <div className="p-6 text-[#5C6B64]">No posts yet.</div>}
              {posts?.map((p) => (
                <div key={p.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid={`blog-admin-row-${p.slug}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><StatusPill status={p.status} /><span className="text-xs text-[#5C6B64]">{p.category}</span></div>
                    <p className="font-medium mt-1 truncate">{p.title}</p>
                    <p className="text-xs text-[#5C6B64] mt-0.5">/blog/{p.slug} · updated {formatDate(p.updated_at)}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {p.status === "published" && (
                      <Button asChild variant="ghost" size="icon" data-testid={`blog-admin-view-${p.slug}`}>
                        <Link to={`/blog/${p.slug}`}><ExternalLink className="w-4 h-4" /></Link>
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/blog/admin/${p.id}`)} data-testid={`blog-admin-edit-${p.slug}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setToDelete(p)} className="hover:text-[#C65D47]" data-testid={`blog-admin-delete-${p.slug}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="blog-admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.title}" will be removed permanently.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="blog-admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#C65D47] hover:bg-[#b04f3b]" data-testid="blog-admin-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
